import { useContext, useState } from "react";
import { APIContext } from "../../../APIContext";

/**
 * Product Provisioner — Function 2 (Phase 2, milestone M5). Update a live Product's embed
 * link. Enter the page handle + the new URL, then Preview: the tool resolves the Shopify Page
 * and detects where its embed lives —
 *   • metafield (custom_fields.powerbi_link, rendered by SG-Standard or a Liquid iframe) → a
 *     one-click, instant API swap;
 *   • hardcoded in the page template (a literal <iframe src="https://…">) → v1 does NOT
 *     auto-write it; it shows the exact find/replace to apply to the live theme by hand;
 *   • otherwise (no embed / a non-PowerBi page_type) → flagged for manual handling.
 * See docs/plans/phase-2-product-provisioner.md.
 */
export default function EditEmbedPage() {
  const context = useContext(APIContext);

  const [handle, setHandle] = useState<string>("");
  const [newUrl, setNewUrl] = useState<string>("");

  const [preview, setPreview] = useState<any | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [busy, setBusy] = useState<"" | "preview" | "update">("");
  const [status, setStatus] = useState<{ type: string; msg: string } | null>(null);

  // Editing any field invalidates a shown preview/result — you can't confirm a stale plan.
  const invalidate = () => {
    if (preview) setPreview(null);
    if (result) setResult(null);
    if (status) setStatus(null);
  };

  const canPreview = Boolean(handle.trim() && newUrl.trim()) && !busy;
  const payload = () => ({ handle: handle.trim(), new_embed_url: newUrl.trim() });

  const runPreview = async () => {
    setBusy("preview");
    setStatus(null);
    setResult(null);
    try {
      const res: any = await context.previewUpdateEmbed(payload());
      const d = res?.data || {};
      if (d.status === "success") {
        setPreview(d.plan);
      } else {
        setStatus({ type: "danger", msg: d.message || "Preview failed." });
      }
    } catch (err: any) {
      console.error(err);
      setStatus({ type: "danger", msg: err?.message || "Unexpected error." });
    } finally {
      setBusy("");
    }
  };

  // Only the metafield path writes (an instant, safe swap). Theme/manual paths are flagged
  // in the preview and never reach this button.
  const runUpdate = async () => {
    if (!preview || preview.path !== "metafield") return;
    setBusy("update");
    setStatus(null);
    try {
      const res: any = await context.updateProductEmbed(payload());
      const d = res?.data || {};
      setResult(d);
      setStatus({
        type: d.status === "success" ? "success" : d.status === "manual" ? "warning" : "danger",
        msg: d.message || (d.status === "success" ? "Updated." : "Update failed."),
      });
      if (d.status === "success") setPreview(null);
    } catch (err: any) {
      console.error(err);
      setStatus({ type: "danger", msg: err?.message || "Unexpected error." });
    } finally {
      setBusy("");
    }
  };

  return (
    <div style={{ fontFamily: "Montserrat, sans-serif", marginTop: "3rem" }}>
      <div className="m0 listings-wrapper middle half-width">
        <h1 style={{ fontWeight: "bold" }}>Update a Product's embed link</h1>
        <p>
          Swap the Power BI / Netlify URL on a Product that's already live in the Portal. Enter its
          page handle and the new URL, then <strong>Preview</strong> to see exactly what will change
          before anything is written.
        </p>

        {/* Page handle */}
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="ee-handle">Page handle</label>
          <input
            id="ee-handle"
            type="text"
            className="form-control"
            placeholder="e.g. custom-wind-river-oral-pouch"
            value={handle}
            onChange={(e) => {
              setHandle(e.target.value);
              invalidate();
            }}
          />
          <small className="text-muted">
            The last part of the page URL — <code>/pages/&lt;handle&gt;</code>.
          </small>
        </div>

        {/* New embed URL */}
        <div style={{ marginTop: "1rem" }} className="form-group">
          <label htmlFor="ee-url">New embed URL</label>
          <input
            id="ee-url"
            type="url"
            className="form-control"
            placeholder="https://app.powerbi.com/view?r=… or https://brightfield-….netlify.app"
            value={newUrl}
            onChange={(e) => {
              setNewUrl(e.target.value);
              invalidate();
            }}
          />
        </div>

        {/* Preview action */}
        <div style={{ display: "flex", marginTop: "1.5rem", alignItems: "center" }}>
          {status && (
            <div className={`alert alert-${status.type}`} style={{ marginRight: "1rem", marginBottom: 0 }}>
              {status.msg}
            </div>
          )}
          <div style={{ marginLeft: "auto" }}>
            <button className="btn btn-secondary btn-sm" disabled={!canPreview} onClick={runPreview}>
              {busy === "preview" ? "Previewing…" : "Preview"}
            </button>
          </div>
        </div>

        {/* Preview panel */}
        {preview && (
          <div style={{ marginTop: "1.5rem", borderTop: "1px solid #eee", paddingTop: "1rem" }}>
            <h4 style={{ fontWeight: "bold" }}>Review — nothing written yet</h4>

            {preview.warnings && preview.warnings.length > 0 && (
              <div className="alert alert-warning">
                <ul style={{ marginBottom: 0 }}>
                  {preview.warnings.map((w: string, i: number) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            <table className="table table-sm">
              <tbody>
                <tr>
                  <th>Page</th>
                  <td>
                    <code>/pages/{preview.handle}</code> &middot; {preview.title} &middot; template{" "}
                    {preview.template_suffix || <span className="text-muted">(default)</span>}
                  </td>
                </tr>
                <tr>
                  <th>Detected embed source</th>
                  <td>
                    {preview.path === "metafield" && (
                      <>
                        <strong>Metafield</strong> — instant API swap{" "}
                        <span className="text-muted">
                          (via {preview.via}; <code>{preview.metafield?.namespace}.{preview.metafield?.key}</code>,{" "}
                          {preview.metafield?.action})
                        </span>
                      </>
                    )}
                    {preview.path === "theme" && (
                      <>
                        <strong>Hardcoded in page template</strong>{" "}
                        <span className="text-muted">— manual theme edit required</span>
                      </>
                    )}
                    {preview.path === "manual" && (
                      <>
                        <strong>Manual</strong> <span className="text-muted">— {preview.reason}</span>
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Current URL</th>
                  <td>
                    {preview.current_url ? (
                      <code style={{ wordBreak: "break-all" }}>{preview.current_url}</code>
                    ) : (
                      <span className="text-muted">(none)</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>New URL</th>
                  <td>
                    <code style={{ wordBreak: "break-all" }}>{preview.new_url || newUrl.trim()}</code>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Metafield path → the one-click write */}
            {preview.path === "metafield" && (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="btn btn-primary btn-sm" disabled={busy === "update"} onClick={runUpdate}>
                  {busy === "update" ? "Updating…" : "Confirm & update"}
                </button>
              </div>
            )}

            {/* Theme path → the exact find/replace to apply by hand */}
            {preview.path === "theme" && (
              <div className="alert alert-warning">
                <strong>Apply this to the live theme manually.</strong> This product's embed URL is
                hardcoded in <code>{preview.template_key}</code>; v1 does not auto-write theme files.
                In the theme editor, open that template and replace:
                <pre style={{ whiteSpace: "pre-wrap", background: "#f7f7f7", padding: "0.5rem", marginTop: "0.5rem" }}>
                  {`- ${preview.replace?.find}\n+ ${preview.replace?.replace_with}`}
                </pre>
              </div>
            )}

            {preview.path === "manual" && (
              <div className="alert alert-warning" style={{ marginBottom: 0 }}>
                This page isn't a recognized metafield embed, so it can't be updated automatically.
                {preview.page_type ? (
                  <>
                    {" "}
                    Its <code>page_type</code> is <strong>{preview.page_type}</strong>.
                  </>
                ) : null}
              </div>
            )}
          </div>
        )}

        {/* Result panel (metafield update) */}
        {result && result.status === "success" && (
          <div style={{ marginTop: "1.5rem", borderTop: "1px solid #eee", paddingTop: "1rem" }}>
            <h4 style={{ fontWeight: "bold" }}>Updated</h4>
            <p style={{ marginBottom: "0.25rem" }}>
              <code>/pages/{result.handle}</code> embed link swapped.
            </p>
            <p style={{ marginBottom: "0.25rem" }}>
              <span className="text-muted">from</span>{" "}
              <code style={{ wordBreak: "break-all" }}>{result.previous_url || "(none)"}</code>
              <br />
              <span className="text-muted">to</span>{" "}
              <code style={{ wordBreak: "break-all" }}>{result.new_url}</code>
            </p>
            <p className="text-muted" style={{ marginBottom: 0 }}>
              provisioning_log: {result.provisioning_log_id}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
